<template>
  <div
    class="modal fade"
    :id="`copyStrategyModal-${Strategy.id}`"
    tabindex="-1"
    aria-labelledby="copyStrategyModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="copyStrategyModalLabel">Copy Strategy</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="copyStrategy($event)">
            <div class="mb-3">
              <label for="StrategyDescription" class="form-label"
                >Strategy Name</label
              >
              <input
                type="text"
                class="form-control"
                id="StrategyDescription"
                v-model="description"
                required
                placeholder="Enter Strategy name"
              />
              <!-- Red warning if name is duplicated -->
              <div v-if="isNameNotValid" class="text-danger mt-1">
                Strategy with such name already exists.
              </div>
            </div>
            <div class="mb-3">
              <label for="facilitySelect" class="form-label"
                >Copy to Facility</label
              >
              <select
                id="facilitySelect"
                class="form-select"
                v-model="facilityId"
                required
              >
                <option value="" disabled>Select a Facility</option>
                <option
                  v-for="facility in Facilities"
                  :key="facility.id"
                  :value="facility.id"
                >
                  {{ facility.name }}
                </option>
              </select>
            </div>
            <div class="d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-secondary me-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isNameNotValid"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  PropType,
  defineComponent,
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
} from 'vue';
import { Modal } from 'bootstrap';
import { Facility, PriceWindowUtilization, Strategy } from '@/api';
import { pricingApi, strategyApi } from '../apiClient';
import eventBus, { EventType } from '@/eventBus';
import { useToast } from 'vue-toastification';
import { isNameNotUniqueWithinFacility } from '@/utils/strategyUtils';
import { AllDay } from '@/utils/dateUtils';

export default defineComponent({
  name: 'StrategyCopyComponent',
  props: {
    Strategy: {
      type: Object as PropType<Strategy>,
      required: true,
    },
    Facilities: {
      type: Array as PropType<Facility[]>,
      required: true,
    },
    ExistingStrategies: {
      type: Array as () => Strategy[],
      required: true,
    },
  },
  setup(props) {
    const description = ref('COPY: ' + (props.Strategy.name || ''));
    const facilityId = ref('');
    const toast = useToast();

    onMounted(() => {
      const modalId = `copyStrategyModal-${props.Strategy.id}`;
      const modalElement = document.getElementById(modalId);
      if (modalElement) {
        new Modal(modalElement);
      }
    });

    onBeforeUnmount(() => {
      const modalId = `copyStrategyModal-${props.Strategy.id}`;
      const modalElement = document.getElementById(modalId);
      if (modalElement) {
        const modalInstance = Modal.getInstance(modalElement);
        if (modalInstance) {
          modalInstance.dispose();
        }
      }
    });

    const isNameNotValid = computed(() => {
      return isNameNotUniqueWithinFacility(
        Number(facilityId.value),
        description.value,
        props.ExistingStrategies
      );
    });

    function buildDefaultUtilization(
      priceWindowId: number
    ): PriceWindowUtilization {
      return {
        priceWindowId,
        dailyUtilizations: [
          {
            dayOfWeek: 0,
            timeWindows: [
              {
                startTime: AllDay.startTime,
                endTime: AllDay.endTime,
                utilizationRanges: [
                  {
                    low: 0,
                    high: 100,
                    pricePercentage: 100,
                    id: 0,
                    usePercent: true,
                  },
                ],
              },
            ],
          },
        ],
      };
    }

    function cloneUtilization(
      utilization: PriceWindowUtilization,
      priceWindowId: number
    ): PriceWindowUtilization {
      return {
        priceWindowId,
        dailyUtilizations:
          utilization.dailyUtilizations?.map((daily) => ({
            ...daily,
            timeWindows:
              daily.timeWindows?.map((timeWindow) => ({
                ...timeWindow,
                utilizationRanges:
                  timeWindow.utilizationRanges?.map((range) => ({
                    ...range,
                    id: 0,
                  })) ?? [],
              })) ?? [],
          })) ?? [],
      };
    }

    async function copyPricingWindows(
      sourceStrategyId: number,
      targetStrategyId: number
    ) {
      const windowsResponse = await pricingApi.pricingGet(sourceStrategyId);
      const sourceWindows = Array.isArray(windowsResponse.data)
        ? windowsResponse.data
        : [];

      if (!sourceWindows.length) {
        const defaultWindowResponse = await pricingApi.pricingPost({
          strategyId: targetStrategyId,
          sortOrder: 0,
          name: 'Default',
          description: 'Default price window and strategy',
          daysIn: 0,
          daysOut: 9999,
        });

        if (defaultWindowResponse.data?.id) {
          await pricingApi.pricingDetailsPost(
            buildDefaultUtilization(defaultWindowResponse.data.id)
          );
        }
        return;
      }

      for (const sourceWindow of sourceWindows) {
        const createdWindowResponse = await pricingApi.pricingPost({
          strategyId: targetStrategyId,
          sortOrder: sourceWindow.sortOrder,
          name: sourceWindow.name,
          description: sourceWindow.description,
          daysIn: sourceWindow.daysIn,
          daysOut: sourceWindow.daysOut,
        });

        const createdWindowId = createdWindowResponse.data?.id;
        if (!createdWindowId) {
          continue;
        }

        if (!sourceWindow.id) {
          await pricingApi.pricingDetailsPost(
            buildDefaultUtilization(createdWindowId)
          );
          continue;
        }

        const sourceDetailsResponse = await pricingApi.pricingDetailsGet(
          sourceWindow.id
        );
        const sourceDetails = sourceDetailsResponse.data;
        const detailsToCreate =
          sourceDetails && typeof sourceDetails === 'object'
            ? cloneUtilization(sourceDetails, createdWindowId)
            : buildDefaultUtilization(createdWindowId);

        await pricingApi.pricingDetailsPost(detailsToCreate);
      }
    }

    async function createStrategyCopyFromClient(): Promise<boolean> {
      const targetFacilityId = Number(facilityId.value);
      const existsResponse = await strategyApi.apiStrategyExistsGet(
        targetFacilityId,
        description.value,
        0
      );

      if (existsResponse.data) {
        toast.error('Strategy with such name already exists.');
        return false;
      }

      const strategyToCreate: Strategy = {
        ...props.Strategy,
        id: undefined,
        facilityId: targetFacilityId,
        name: description.value,
        inUse: false,
      };

      const createResponse = await strategyApi.apiStrategyPost(
        strategyToCreate
      );
      const createdStrategyId = createResponse.data?.id;

      if (!createdStrategyId || !props.Strategy.id) {
        toast.error('Failed to copy strategy.');
        return false;
      }

      await copyPricingWindows(props.Strategy.id, createdStrategyId);
      return true;
    }

    async function copyStrategy() {
      try {
        const response = await strategyApi.apiStrategyCopyGet(
          props.Strategy.facilityId,
          props.Strategy.id,
          facilityId.value,
          description.value
        );

        if (response.status >= 200 && response.status < 300) {
          const copyResults = Array.isArray(response.data) ? response.data : [];
          const failedCopy = copyResults.find(
            (result) => result.resultCode !== 200
          );

          if (failedCopy) {
            toast.error(failedCopy.resultMessage ?? 'Failed to copy strategy.');
            return;
          }

          const wasCopiedByServer =
            copyResults.length > 0 &&
            copyResults.every((result) => result.resultCode === 200);
          const wasCopied =
            wasCopiedByServer || (await createStrategyCopyFromClient());

          if (!wasCopied) {
            return;
          }

          eventBus.emit(EventType.RefreshStrategies);
          toast.success('Strategy Copied Successfully!');

          const modalId = `copyStrategyModal-${props.Strategy.id}`;
          const modalElement = document.getElementById(modalId);
          if (modalElement) {
            const bootstrapModal =
              Modal.getInstance(modalElement) || new Modal(modalElement);
            bootstrapModal.hide();
          }
        }
      } catch (error) {
        console.error('Error copying Strategy:', error);
      }
    }

    return { description, facilityId, copyStrategy, isNameNotValid };
  },
});
</script>

<style scoped>
.modal-dialog-centered {
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
}

.modal-content {
  border-radius: 8px;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
}

.form-check-label {
  margin-left: 5px;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004085;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}
</style>
